/**
 * Lecture du fichier d'un spot et de la prévisualisation de son remplacement.
 *
 * Pur, sans React : la carte « Média associé » de la fiche campagne s'appuie
 * dessus, et les décisions d'affichage (faut-il confirmer ? quel ton ?) se
 * testent sans monter le composant.
 */
import type { SpotMediaInfo, SpotMediaReplacePreview } from './adsApi.js'

/**
 * Ce que l'écran fait d'une prévisualisation :
 *   - `UNCHANGED` : le fichier choisi est déjà celui du spot, rien à écrire ;
 *   - `BLOCKED` : au moins un bloc ne peut pas être recalé, rien n'est proposé ;
 *   - `CONFIRM` : des blocs changent de longueur, l'opérateur valide d'abord ;
 *   - `DIRECT`  : la grille ne bouge pas, le fichier est remplacé aussitôt,
 *                 comme pour un programme.
 *
 * Deux fichiers au même nom (`.mov` et `.mp4`) ne donnent pas de
 * prévisualisation : le serveur la refuse avec un message qui les nomme.
 */
export type SpotReplaceDecision = 'UNCHANGED' | 'BLOCKED' | 'CONFIRM' | 'DIRECT'

export function spotReplaceDecision(preview: SpotMediaReplacePreview): SpotReplaceDecision {
  if (preview.unchanged) return 'UNCHANGED'
  if (preview.blockers.length > 0) return 'BLOCKED'
  if (preview.resizedCount > 0) return 'CONFIRM'
  return 'DIRECT'
}

function seconds(value: number): string {
  const rounded = Math.round(value * 10) / 10
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1).replace('.', ',')} s`
}

function plural(count: number, singular: string, pluralForm: string): string {
  return `${count} ${count > 1 ? pluralForm : singular}`
}

/**
 * Phrase de tête de la confirmation. L'écart annoncé est celui des blocs, pas
 * celui du spot : une transition modifiée depuis la pose ferait sinon promettre
 * à l'infoservice une seconde qu'il ne cédera pas.
 */
export function spotReplaceSummary(preview: SpotMediaReplacePreview): string {
  const head = `${seconds(preview.previousDurationSec)} → ${seconds(preview.durationSec)}`
  const resized = preview.blocks.filter(block => block.side !== null)
  if (resized.length === 0) return `${head} · aucun bloc à venir ne change de longueur`

  const blocks = plural(resized.length, 'bloc à venir recalé', 'blocs à venir recalés')
  const deltas = new Set(resized.map(block => Math.round(block.deltaSec * 1_000)))
  if (deltas.size !== 1) return `${head} · ${blocks} · l’infoservice voisin absorbe l’écart de chacun`

  const delta = resized[0].deltaSec
  const effect = delta > 0 ? `cède ${seconds(delta)}` : `récupère ${seconds(-delta)}`
  return `${head} · ${blocks} · l’infoservice voisin ${effect} à chaque bloc`
}

/** Effet d'un bloc recalé sur son infoservice, pour la liste détaillée. */
export function spotReplaceBlockEffect(block: SpotMediaReplacePreview['blocks'][number]): string {
  if (block.side === null) return 'longueur inchangée'
  const neighbour = block.side === 'AFTER' ? 'infoservice suivant' : 'infoservice précédent'
  return block.deltaSec > 0
    ? `${neighbour} réduit de ${seconds(block.deltaSec)}`
    : `${neighbour} allongé de ${seconds(-block.deltaSec)}`
}

export type SpotMediaTone = 'success' | 'warning' | 'danger' | 'muted'

/**
 * Ton de la vignette et du badge. Comme pour un programme, le ton suit les
 * remontées et pas le seul statut : un fichier lisible dont la durée ne
 * correspond plus à celle du spot mérite l'orange.
 */
export function spotMediaTone(info: SpotMediaInfo | null): SpotMediaTone {
  if (!info) return 'muted'
  if (info.status !== 'OK') return 'danger'
  return info.issues.length > 0 ? 'warning' : 'success'
}

export function spotMediaStatusLabel(info: SpotMediaInfo | null): string {
  if (!info) return 'Lecture…'
  switch (info.status) {
    case 'MISSING': return 'Manquant'
    case 'NOT_SET': return 'Aucun'
    case 'ERROR':   return 'Illisible'
    case 'DUPLICATE': return 'Doublon'
    default:        return info.issues.length > 0 ? 'Durée ?' : 'OK'
  }
}
